import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Breadcrumbs from "@/components/Breadcrumbs";
import Footer from "@/components/Footer";
import { GraduationCap, BookOpen, Award, Users } from "lucide-react";
import { COPY } from "@/content/copy";
import WaitlistForm from "@/components/WaitlistForm";

const PayMonthlyEducation = () => {
  return (
    <>
      <Helmet>
        <title>Pay Monthly for Education | BNPL Education Courses UAE | Flexaura</title>
        <meta name="description" content="Pay monthly for education & courses in UAE. BNPL for schools, universities, training programs. Flexible education financing from AED 2K-200K+. Invest in learning today." />
        <meta name="keywords" content="pay monthly education UAE, BNPL education, buy now pay later education, education financing UAE, pay monthly courses, school fees installments UAE, university fees BNPL, Dubai education finance, Abu Dhabi education BNPL" />
        <link rel="canonical" href="https://flexaura.me/pay-monthly-education" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage", 
            "name": "Pay Monthly for Education | BNPL Education UAE",
            "description": "Pay monthly for education courses, school fees, and training programs in UAE with flexible BNPL and installment options from AED 2,000 to 200,000+",
            "url": "https://flexaura.me/pay-monthly-education",
            "mainEntity": {
              "@type": "FinancialService",
              "name": "Education Payment Plans",
              "description": "Monthly payment options for education including school fees, courses, and training programs",
              "serviceType": ["Buy Now Pay Later", "Education Financing", "Monthly Education Payments"],
              "areaServed": { "@type": "Country", "name": "United Arab Emirates" }
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Navigation />
        <Breadcrumbs />
        
        <main>
          <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-accent/10 via-background to-primary/10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-accent/10 rounded-full px-4 py-2 mb-6">
                <GraduationCap className="w-4 h-4 text-accent" />
                <span className="text-sm font-semibold text-accent">Education BNPL</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6 leading-tight">
                Pay Monthly for Education in UAE
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                {COPY.sublineByVertical.education}
              </p>

              <WaitlistForm
                source="pay-monthly-education"
                placeholder="Get early access to education BNPL..."
                buttonText="Join Waitlist"
                className="max-w-md mx-auto mb-12"
              />
            </div>
          </section>

          {/* Education Options */}
          <section className="py-16 px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-center mb-12">Pay Monthly for Any Educational Investment</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {[
                  { icon: GraduationCap, title: "University & Higher Ed", description: "Degree programs, MBA courses, and university fees" },
                  { icon: BookOpen, title: "Schools & K-12", description: "Private school fees, international curricula, and boarding" },
                  { icon: Award, title: "Professional Training", description: "Certifications, workshops, and skill development courses" },
                  { icon: Users, title: "Online Learning", description: "Digital courses, bootcamps, and e-learning platforms" }
                ].map((item, index) => (
                  <div key={index} className="text-center p-6 rounded-2xl card-shadow bg-card">
                    <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <item.icon className="w-8 h-8 text-accent" />
                    </div>
                    <h3 className="text-lg font-heading font-semibold mb-3">{item.title}</h3>
                    <p className="text-muted-foreground text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Benefits */}
          <section className="py-16 px-4 bg-muted/50">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-heading font-bold mb-12">Why Pay Monthly for Education?</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  "Start learning immediately without waiting to save up the full amount",
                  "Spread education costs over manageable monthly payments", 
                  "Access better schools and courses that were previously out of reach"
                ].map((benefit, index) => (
                  <div key={index} className="p-6 bg-card rounded-xl">
                    <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-4 text-accent font-bold text-xl">{index + 1}</div>
                    <p className="text-muted-foreground">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-16 px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-heading font-bold text-center mb-12">Education BNPL FAQ</h2>
              <div className="space-y-6">
                {[
                  { q: "Can I pay monthly for private school fees in UAE?", a: "Yes, Flexaura partners with schools and education providers to offer monthly payment plans for tuition fees, making private education more accessible to families in Dubai, Abu Dhabi, and across the UAE." },
                  { q: "What's the maximum amount for education financing?", a: "Education financing goes up to AED 200,000+ depending on the program and your eligibility, covering everything from professional certifications to full degree programs." },
                  { q: "Do you offer payment plans for international schools?", a: "Yes, we work with various international schools in the UAE including British, American, and IB curricula schools to provide flexible payment options for families." }
                ].map((faq, index) => (
                  <div key={index} className="border-b pb-4">
                    <h3 className="font-semibold mb-2 text-lg">{faq.q}</h3>
                    <p className="text-muted-foreground">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default PayMonthlyEducation;
